import React, { useState, useEffect, useMemo } from 'react';
import { 
  Euro, TrendingUp, TrendingDown, Calculator, Users, UserCheck, 
  Receipt, FileText, Check, X, Clock, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle, DollarSign, Percent, Building2, Trash2,
  Eye, Edit2, Download, ArrowLeft, Plus, RefreshCw, Link2, Calendar, Filter, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get available years for filter
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2];
};

// Helper to get months
const MONTHS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' }
];

const MEMBER_TYPES = [
  { value: 'all', label: 'Tous les types' },
  { value: 'affiliate', label: 'Affiliés' },
  { value: 'analyst', label: 'Analystes' },
  { value: 'chiffreur', label: 'Chiffreurs' },
  { value: 'partner', label: 'Partenaires' }
];

export default function AccountingDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [estimations, setEstimations] = useState(null);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientInvoicesSummary, setClientInvoicesSummary] = useState(null);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [collaboratorModal, setCollaboratorModal] = useState(false);
  const [editCommissionModal, setEditCommissionModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // NEW: Date and type filters
  const [filterPeriod, setFilterPeriod] = useState('all'); // 'all', 'day', 'month', 'year'
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState((new Date().getMonth() + 1).toString());
  const [filterDay, setFilterDay] = useState(new Date().getDate().toString());
  const [filterMemberType, setFilterMemberType] = useState('all');
  
  const [expandedSections, setExpandedSections] = useState({
    affiliates: true,
    analysts: true,
    chiffreurs: true,
    partners: true,
    pending: true,
    validated: true,
    paid: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, estimationsRes, clientInvoicesRes, clientSummaryRes] = await Promise.all([
        axios.get(`${API}/admin/accounting/real-time-dashboard`),
        axios.get(`${API}/admin/accounting/profit-estimation`),
        axios.get(`${API}/admin/client-invoices`),
        axios.get(`${API}/admin/client-invoices/summary`)
      ]);
      setDashboard(dashboardRes.data);
      setEstimations(estimationsRes.data);
      setClientInvoices(clientInvoicesRes.data);
      setClientInvoicesSummary(clientSummaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Filter function for commissions by date and type
  const filterByDateAndType = (items, dateField = 'created_at') => {
    if (!items) return [];
    
    return items.filter(item => {
      // Filter by member type
      if (filterMemberType !== 'all') {
        const itemType = item.type || item.collaborator_type;
        if (itemType !== filterMemberType) return false;
      }
      
      // Filter by date
      if (filterPeriod === 'all') return true;
      
      const itemDate = new Date(item[dateField]);
      if (isNaN(itemDate.getTime())) return true; // If no valid date, include
      
      const itemYear = itemDate.getFullYear();
      const itemMonth = itemDate.getMonth() + 1;
      const itemDay = itemDate.getDate();
      
      if (filterPeriod === 'year') {
        return itemYear === parseInt(filterYear);
      }
      
      if (filterPeriod === 'month') {
        return itemYear === parseInt(filterYear) && itemMonth === parseInt(filterMonth);
      }
      
      if (filterPeriod === 'day') {
        return itemYear === parseInt(filterYear) && 
               itemMonth === parseInt(filterMonth) && 
               itemDay === parseInt(filterDay);
      }
      
      return true;
    });
  };

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    if (!dashboard) return null;
    
    // Get all commissions from different categories
    let allCommissions = [];
    
    if (dashboard.affiliates) {
      dashboard.affiliates.forEach(a => {
        (a.commissions || []).forEach(c => allCommissions.push({ ...c, type: 'affiliate' }));
      });
    }
    if (dashboard.analysts) {
      dashboard.analysts.forEach(a => {
        (a.notes || []).forEach(c => allCommissions.push({ ...c, type: 'analyst' }));
      });
    }
    if (dashboard.chiffreurs) {
      dashboard.chiffreurs.forEach(a => {
        (a.notes || []).forEach(c => allCommissions.push({ ...c, type: 'chiffreur' }));
      });
    }
    if (dashboard.partners) {
      dashboard.partners.forEach(a => {
        (a.commissions || []).forEach(c => allCommissions.push({ ...c, type: 'partner' }));
      });
    }
    
    const filtered = filterByDateAndType(allCommissions);
    
    return {
      total: filtered.reduce((sum, c) => sum + (c.amount || 0), 0),
      pending: filtered.filter(c => c.status === 'pending').reduce((sum, c) => sum + (c.amount || 0), 0),
      validated: filtered.filter(c => c.status === 'validated').reduce((sum, c) => sum + (c.amount || 0), 0),
      paid: filtered.filter(c => c.status === 'paid').reduce((sum, c) => sum + (c.amount || 0), 0),
      count: filtered.length
    };
  }, [dashboard, filterPeriod, filterYear, filterMonth, filterDay, filterMemberType]);

  const validateCommission = async (id) => {
    try {
      await axios.put(`${API}/admin/accounting/commissions/${id}/validate`);
      toast.success('Commission validée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const payCommission = async (id) => {
    try {
      await axios.put(`${API}/admin/accounting/commissions/${id}/pay`);
      toast.success('Commission marquée comme payée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteCommission = async (id) => {
    if (!window.confirm('Supprimer cette note de frais ?')) return;
    try {
      await axios.delete(`${API}/admin/accounting/commissions/${id}`);
      toast.success('Note supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const bulkValidate = async () => {
    if (!selectedCommissions.length) return;
    try {
      await axios.post(`${API}/admin/accounting/commissions/bulk-validate`, { ids: selectedCommissions });
      toast.success(`${selectedCommissions.length} commission(s) validée(s)`);
      setSelectedCommissions([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const bulkPay = async () => {
    if (!selectedCommissions.length) return;
    try {
      await axios.post(`${API}/admin/accounting/commissions/bulk-pay`, { ids: selectedCommissions });
      toast.success(`${selectedCommissions.length} commission(s) payée(s)`);
      setSelectedCommissions([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const bulkDelete = async () => {
    if (!selectedCommissions.length) return;
    if (!window.confirm(`Supprimer ${selectedCommissions.length} note(s) de frais ?`)) return;
    try {
      await axios.post(`${API}/admin/accounting/commissions/bulk-delete`, { ids: selectedCommissions });
      toast.success(`${selectedCommissions.length} note(s) supprimée(s)`);
      setSelectedCommissions([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteAllCommissions = async () => {
    if (!window.confirm('ATTENTION: Supprimer TOUTES les notes de frais ? Cette action est irréversible.')) return;
    if (!window.confirm('Êtes-vous vraiment sûr ? Toutes les données seront perdues.')) return;
    try {
      await axios.delete(`${API}/admin/accounting/commissions/all`);
      toast.success('Toutes les notes de frais ont été supprimées');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateCommission = async () => {
    if (!editingCommission) return;
    try {
      await axios.put(`${API}/admin/accounting/commissions/${editingCommission.id}`, editingCommission);
      toast.success('Note de frais mise à jour');
      setEditCommissionModal(false);
      setEditingCommission(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const downloadCommissionPDF = async (commission) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Vous devez être connecté pour télécharger');
        return;
      }
      
      const response = await fetch(`${API}/admin/accounting/commissions/${commission.id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Get the PDF as a Blob
      const blob = await response.blob();
      
      // Create a URL for the Blob and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `note_frais_${commission.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const downloadClientInvoicePDF = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Vous devez être connecté pour télécharger');
        return;
      }
      
      const response = await fetch(`${API}/admin/client-invoices/${invoice.id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée avec succès');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement de la facture');
    }
  };

  // Export comptable (CSV seul ou ZIP = CSV + tous les PDF) pour la période choisie
  const [exportYear, setExportYear] = useState(new Date().getFullYear().toString());
  const [exportMonth, setExportMonth] = useState((new Date().getMonth() + 1).toString());
  const [exporting, setExporting] = useState(null);

  const sendPeriodToAccountant = async () => {
    try {
      setExporting('mail');
      const res = await axios.post(`${API}/admin/accounting/send-to-accountant`, null, { params: { year: exportYear, month: exportMonth } });
      toast.success(`Export ${res.data.period} envoyé à ${res.data.to_email} (${res.data.invoice_count} facture(s))`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'envoi au comptable");
    } finally {
      setExporting(null);
    }
  };

  const exportClientInvoices = async (format) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Vous devez être connecté pour exporter'); return; }
      setExporting(format);
      const params = new URLSearchParams({ format });
      if (exportYear !== 'all') {
        params.set('year', exportYear);
        if (exportMonth !== 'all') params.set('month', exportMonth);
      }
      const response = await fetch(`${API}/admin/client-invoices/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Export impossible');
      const count = response.headers.get('X-Invoice-Count');
      const label = exportYear === 'all' ? 'toutes' : exportMonth === 'all' ? exportYear : `${exportYear}-${String(exportMonth).padStart(2, '0')}`;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factures_${label}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(count === '0' ? 'Aucune facture sur cette période (fichier vide généré)' : `${count} facture(s) exportée(s)`);
    } catch (error) {
      toast.error("Erreur lors de l'export comptable");
    } finally {
      setExporting(null);
    }
  };


  // Preview PDF in new tab (for Notes de Frais)
  const previewCommissionPDF = async (commission) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Vous devez être connecté');
        return;
      }
      
      const response = await fetch(`${API}/admin/accounting/commissions/${commission.id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  // Preview PDF in new tab (for Client Invoices)
  const previewClientInvoicePDF = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Vous devez être connecté');
        return;
      }
      
      const response = await fetch(`${API}/admin/client-invoices/${invoice.id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  const openCollaboratorDetail = (collaborator, type) => {
    setSelectedCollaborator({ ...collaborator, collaborator_type: type });
    setCollaboratorModal(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-red-100 text-red-700">À valider</Badge>;
      case 'validated':
        return <Badge className="bg-amber-100 text-amber-700">À payer</Badge>;
      case 'paid':
        return <Badge className="bg-emerald-100 text-emerald-700">Payé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCollaboratorCommissions = (collaboratorId) => {
    const allCommissions = [
      ...(dashboard?.by_type?.affiliate?.items || []),
      ...(dashboard?.by_type?.analyst?.items || []),
      ...(dashboard?.by_type?.chiffreur?.items || []),
      ...(dashboard?.by_type?.partner?.items || [])
    ];
    return allCommissions.filter(c => c.beneficiary_id === collaboratorId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const byType = dashboard?.by_type || {};
  const details = dashboard?.details || {};

  // All commissions for the list view
  const allCommissions = [
    ...(byType.affiliate?.items || []),
    ...(byType.analyst?.items || []),
    ...(byType.chiffreur?.items || []),
    ...(byType.partner?.items || [])
  ];

  const filteredCommissions = filterStatus === 'all' 
    ? allCommissions 
    : allCommissions.filter(c => c.status === filterStatus);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white shadow-sm mb-4 flex-wrap">
          <TabsTrigger value="overview" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />Vue Temps Réel
          </TabsTrigger>
          <TabsTrigger value="estimations" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <Calculator className="w-4 h-4 mr-2" />Estimation Bénéfice
          </TabsTrigger>
          <TabsTrigger value="client-invoices" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />Factures Clients
          </TabsTrigger>
          <TabsTrigger value="commissions" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <Receipt className="w-4 h-4 mr-2" />Notes de Frais
          </TabsTrigger>
          <TabsTrigger value="details" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />Collaborateurs
          </TabsTrigger>
        </TabsList>

        {/* FILTRES PAR DATE ET TYPE */}
        <Card className="mb-4 border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-700">Filtres</span>
              </div>
              
              {/* Period filter */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Période:</Label>
                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                  <SelectTrigger className="w-[130px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tout</SelectItem>
                    <SelectItem value="day">Par jour</SelectItem>
                    <SelectItem value="month">Par mois</SelectItem>
                    <SelectItem value="year">Par année</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Year filter */}
              {filterPeriod !== 'all' && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Année:</Label>
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="w-[100px] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getYearOptions().map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Month filter */}
              {(filterPeriod === 'month' || filterPeriod === 'day') && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Mois:</Label>
                  <Select value={filterMonth} onValueChange={setFilterMonth}>
                    <SelectTrigger className="w-[130px] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Day filter */}
              {filterPeriod === 'day' && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Jour:</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    max="31"
                    value={filterDay}
                    onChange={(e) => setFilterDay(e.target.value)}
                    className="w-[70px] bg-white"
                  />
                </div>
              )}
              
              {/* Member type filter */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Type:</Label>
                <Select value={filterMemberType} onValueChange={setFilterMemberType}>
                  <SelectTrigger className="w-[150px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEMBER_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reset button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setFilterPeriod('all');
                  setFilterMemberType('all');
                }}
                className="ml-auto"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Réinitialiser
              </Button>
            </div>
            
            {/* Filtered stats summary */}
            {filteredStats && filterPeriod !== 'all' && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-blue-700">
                    <strong>Résultat filtré:</strong> {filteredStats.count} notes de frais
                  </span>
                  <span className="text-gray-600">
                    Total: <strong>{formatPrice(filteredStats.total)}</strong>
                  </span>
                  <span className="text-amber-600">
                    En attente: <strong>{formatPrice(filteredStats.pending)}</strong>
                  </span>
                  <span className="text-green-600">
                    Validées: <strong>{formatPrice(filteredStats.validated)}</strong>
                  </span>
                  <span className="text-emerald-600">
                    Payées: <strong>{formatPrice(filteredStats.paid)}</strong>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* === VUE TEMPS RÉEL === */}
        <TabsContent value="overview">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">CA Encaissé</span>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{formatPrice(summary.revenue?.paid_ttc)}</p>
                <p className="text-xs text-emerald-600/70">{summary.revenue?.paid_count || 0} factures</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">En Attente</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatPrice(summary.revenue?.pending_ttc)}</p>
                <p className="text-xs text-amber-600/70">Non encaissé</p>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Comm. Payées</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{formatPrice(summary.commissions?.paid)}</p>
                <p className="text-xs text-green-600/70">Déjà versées</p>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Comm. À Payer</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{formatPrice(summary.commissions?.total_due)}</p>
                <p className="text-xs text-orange-600/70">Reste à verser</p>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">TVA à Reverser</span>
                </div>
                <p className={`text-2xl font-bold ${summary.tva?.balance >= 0 ? 'text-purple-600' : 'text-emerald-600'}`}>
                  {formatPrice(Math.abs(summary.tva?.balance || 0))}
                </p>
                <p className="text-xs text-purple-600/70">{summary.tva?.balance >= 0 ? 'À payer' : 'Crédit TVA'}</p>
              </CardContent>
            </Card>

            <Card className={`border-2 ${summary.profit?.projected >= 0 ? 'border-blue-400 bg-blue-50' : 'border-red-400 bg-red-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Euro className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">Bénéfice Net</span>
                </div>
                <p className={`text-2xl font-bold ${summary.profit?.projected >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatPrice(summary.profit?.projected)}
                </p>
                <p className="text-xs text-muted-foreground">Après toutes charges</p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Détail Revenus
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">CA TTC encaissé</span>
                    <span className="font-semibold text-emerald-600">{formatPrice(summary.revenue?.paid_ttc)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">CA HT</span>
                    <span className="font-medium">{formatPrice(summary.revenue?.paid_ht)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">TVA collectée</span>
                    <span className="font-medium text-purple-600">{formatPrice(summary.tva?.collected)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Frais Stripe (~2,9%)</span>
                    <span className="font-medium text-red-500">-{formatPrice(summary.stripe_fees)}</span>
                  </div>
                  {summary.spf_costs > 0 && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground flex items-center gap-1">
                        Frais SPF (Radar Foncier)
                        <span className="text-xs text-gray-400 ml-1" title="Service d'État exonéré de TVA (art. 256 B CGI)">ⓘ</span>
                      </span>
                      <span className="font-medium text-red-500">-{formatPrice(summary.spf_costs)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Expenses Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  Détail Charges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Achats/Fournisseurs HT</span>
                    <span className="font-medium text-red-600">-{formatPrice(summary.expenses?.ht)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">TVA déductible</span>
                    <span className="font-medium text-emerald-600">+{formatPrice(summary.tva?.deductible)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b items-center">
                    <span className="text-muted-foreground">Commissions Affiliés</span>
                    <div className="text-right">
                      <span className="font-medium text-red-600">-{formatPrice(byType.affiliate?.total || 0)}</span>
                      <Badge className="ml-2 bg-amber-100 text-amber-700">{byType.affiliate?.count || 0}</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between py-2 border-b items-center">
                    <span className="text-muted-foreground">Notes Analystes</span>
                    <div className="text-right">
                      <span className="font-medium text-red-600">-{formatPrice(byType.analyst?.total || 0)}</span>
                      <Badge className="ml-2 bg-blue-100 text-blue-700">{byType.analyst?.count || 0}</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between py-2 border-b items-center">
                    <span className="text-muted-foreground">Notes Chiffreurs</span>
                    <div className="text-right">
                      <span className="font-medium text-red-600">-{formatPrice(byType.chiffreur?.total || 0)}</span>
                      <Badge className="ml-2 bg-emerald-100 text-emerald-700">{byType.chiffreur?.count || 0}</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between py-2 border-b items-center">
                    <span className="text-muted-foreground">Commissions Partenaires</span>
                    <div className="text-right">
                      <span className="font-medium text-red-600">-{formatPrice(byType.partner?.total || 0)}</span>
                      <Badge className="ml-2 bg-purple-100 text-purple-700">{byType.partner?.count || 0}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profit Calculation - Clearer presentation */}
          <Card className="mt-6 border-2 border-navy">
            <CardHeader className="bg-navy text-white">
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Calcul du Bénéfice - Résumé
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Step by step calculation */}
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                  <span className="font-medium">CA HT (Chiffre d'Affaires)</span>
                  <span className="text-xl font-bold text-emerald-600">+{formatPrice(summary.revenue?.paid_ht)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">Frais Stripe (~2.9%)</span>
                  <span className="text-xl font-bold text-red-600">-{formatPrice(summary.stripe_fees)}</span>
                </div>
                {summary.spf_costs > 0 && (
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="font-medium">Frais SPF — Radar Foncier <span className="text-xs text-gray-400">(HT=TTC, 0% TVA)</span></span>
                    <span className="text-xl font-bold text-red-600">-{formatPrice(summary.spf_costs)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg border-t-2 border-navy">
                  <span className="font-bold">= Marge avant commissions</span>
                  <span className="text-xl font-bold text-navy">{formatPrice(summary.profit?.before_commissions)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Commissions déjà payées</span>
                  <span className="text-xl font-bold text-green-600">-{formatPrice(summary.commissions?.paid)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                  <span className="font-bold">= Solde actuel (en banque)</span>
                  <span className="text-xl font-bold text-blue-600">{formatPrice(summary.profit?.after_paid_commissions)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                  <span className="font-medium">Commissions restant à payer</span>
                  <span className="text-xl font-bold text-orange-600">-{formatPrice(summary.commissions?.total_due)}</span>
                </div>
                <div className={`flex justify-between items-center p-4 rounded-lg border-2 ${summary.profit?.projected >= 0 ? 'bg-emerald-100 border-emerald-400' : 'bg-red-100 border-red-400'}`}>
                  <span className="font-bold text-lg">= BÉNÉFICE NET PROJETÉ</span>
                  <span className={`text-2xl font-bold ${summary.profit?.projected >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatPrice(summary.profit?.projected)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ESTIMATION BÉNÉFICE === */}
        <TabsContent value="estimations">
          <div className="space-y-6">
            {/* Defaults */}
            <Card className="bg-slate-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Paramètres par défaut</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Commission Affilié</p>
                    <p className="text-xl font-bold text-amber-600">{Math.round((estimations?.defaults?.affiliate_rate || 0.15) * 100)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Note Analyste</p>
                    <p className="text-xl font-bold text-blue-600">{estimations?.defaults?.analyst_amount || 30}€</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Note Chiffreur</p>
                    <p className="text-xl font-bold text-emerald-600">{estimations?.defaults?.chiffreur_amount || 50}€</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Frais Stripe</p>
                    <p className="text-xl font-bold text-red-500">{((estimations?.defaults?.stripe_fee_percent || 0.029) * 100).toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">TVA</p>
                    <p className="text-xl font-bold text-purple-600">{Math.round((estimations?.defaults?.tva_rate || 0.20) * 100)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FLASH */}
            <Card>
              <CardHeader className="bg-blue-600 text-white">
                <CardTitle>FLASH - 97€ TTC (1 audit)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Scénario</th>
                      <th className="text-right p-3">Stripe</th>
                      <th className="text-right p-3">TVA</th>
                      <th className="text-right p-3">Analyste</th>
                      <th className="text-right p-3">Affilié</th>
                      <th className="text-right p-3 bg-emerald-100">Bénéfice</th>
                      <th className="text-right p-3">Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(estimations?.estimations?.flash || {}).map(([key, data]) => (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{key === 'base' ? 'Vous seul' : key === 'with_analyst' ? '+ Analyste' : key === 'with_affiliate' ? '+ Affilié' : '+ Analyste + Affilié'}</td>
                        <td className="p-3 text-right text-red-500">-{formatPrice(data.stripe_fee)}</td>
                        <td className="p-3 text-right text-purple-500">-{formatPrice(data.tva)}</td>
                        <td className="p-3 text-right text-blue-500">{data.analyst_cost > 0 ? `-${formatPrice(data.analyst_cost)}` : '-'}</td>
                        <td className="p-3 text-right text-amber-500">{data.affiliate_cost > 0 ? `-${formatPrice(data.affiliate_cost)}` : '-'}</td>
                        <td className={`p-3 text-right font-bold bg-emerald-50 ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatPrice(data.profit)}</td>
                        <td className="p-3 text-right">{data.margin_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* PRO */}
            <Card>
              <CardHeader className="bg-amber-500 text-white">
                <CardTitle>MARCHAND PRO - 247€ TTC/mois (4 audits)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Scénario</th>
                      <th className="text-right p-3">Stripe</th>
                      <th className="text-right p-3">TVA</th>
                      <th className="text-right p-3">Analyste</th>
                      <th className="text-right p-3">Chiffreur</th>
                      <th className="text-right p-3">Affilié</th>
                      <th className="text-right p-3 bg-emerald-100">Bénéfice</th>
                      <th className="text-right p-3">Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(estimations?.estimations?.pro || {}).map(([key, data]) => (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">
                          {key === 'base' ? 'Vous seul' :
                           key === 'with_1_analyst' ? '+ 1 Analyste' :
                           key === 'with_2_analysts' ? '+ 2 Analystes' :
                           key === 'with_3_analysts' ? '+ 3 Analystes' :
                           key === 'with_4_analysts' ? '+ 4 Analystes' :
                           key === 'with_1_chiffreur' ? '+ 1 Chiffreur' :
                           key === 'with_2_chiffreurs' ? '+ 2 Chiffreurs' :
                           key === 'with_1_analyst_1_chiffreur' ? '+ 1A + 1C' :
                           key === 'with_affiliate' ? '+ Affilié' :
                           key === 'with_4_analysts_affiliate' ? '+ 4 Analystes + Affilié' : key}
                        </td>
                        <td className="p-3 text-right text-red-500">-{formatPrice(data.stripe_fee)}</td>
                        <td className="p-3 text-right text-purple-500">-{formatPrice(data.tva)}</td>
                        <td className="p-3 text-right text-blue-500">{data.analyst_cost > 0 ? `-${formatPrice(data.analyst_cost)}` : '-'}</td>
                        <td className="p-3 text-right text-emerald-500">{data.chiffreur_cost > 0 ? `-${formatPrice(data.chiffreur_cost)}` : '-'}</td>
                        <td className="p-3 text-right text-amber-500">{data.affiliate_cost > 0 ? `-${formatPrice(data.affiliate_cost)}` : '-'}</td>
                        <td className={`p-3 text-right font-bold bg-emerald-50 ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatPrice(data.profit)}</td>
                        <td className="p-3 text-right">{data.margin_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* MASTER */}
            <Card>
              <CardHeader className="bg-purple-600 text-white">
                <CardTitle>MASTER VIP - 497€ TTC/mois (illimité)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Scénario</th>
                      <th className="text-right p-3">Stripe</th>
                      <th className="text-right p-3">TVA</th>
                      <th className="text-right p-3">Analyste</th>
                      <th className="text-right p-3">Chiffreur</th>
                      <th className="text-right p-3">Affilié</th>
                      <th className="text-right p-3 bg-emerald-100">Bénéfice</th>
                      <th className="text-right p-3">Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(estimations?.estimations?.master || {}).map(([key, data]) => (
                      <tr key={key} className={`border-b hover:bg-gray-50 ${data.profit < 0 ? 'bg-red-50' : ''}`}>
                        <td className="p-3 font-medium">
                          {key === 'base' ? 'Vous seul' : 
                           key === 'typical' ? 'Cas Moyen (4A+1C)' :
                           key === 'full_team' ? 'Full Team (6A+2C+Aff)' :
                           key}
                        </td>
                        <td className="p-3 text-right text-red-500">-{formatPrice(data.stripe_fee)}</td>
                        <td className="p-3 text-right text-purple-500">-{formatPrice(data.tva)}</td>
                        <td className="p-3 text-right text-blue-500">{data.analyst_cost > 0 ? `-${formatPrice(data.analyst_cost)}` : '-'}</td>
                        <td className="p-3 text-right text-emerald-500">{data.chiffreur_cost > 0 ? `-${formatPrice(data.chiffreur_cost)}` : '-'}</td>
                        <td className="p-3 text-right text-amber-500">{data.affiliate_cost > 0 ? `-${formatPrice(data.affiliate_cost)}` : '-'}</td>
                        <td className={`p-3 text-right font-bold bg-emerald-50 ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatPrice(data.profit)}</td>
                        <td className="p-3 text-right">{data.margin_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            {/* RADAR FONCIER */}
            <Card>
              <CardHeader className="bg-orange-500 text-white">
                <CardTitle>RADAR FONCIER - 49€ TTC (forfait unique)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Scénario</th>
                      <th className="text-right p-3">Stripe</th>
                      <th className="text-right p-3">TVA</th>
                      <th className="text-right p-3 text-orange-600">SPF (0% TVA)</th>
                      <th className="text-right p-3">Affilié</th>
                      <th className="text-right p-3 bg-emerald-100">Bénéfice</th>
                      <th className="text-right p-3">Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(estimations?.estimations?.radar || {}).map(([key, data]) => (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">
                          {key === 'base' ? 'Vous seul' : key === 'with_affiliate' ? '+ Affilié' : key}
                        </td>
                        <td className="p-3 text-right text-red-500">-{formatPrice(data.stripe_fee)}</td>
                        <td className="p-3 text-right text-purple-500">-{formatPrice(data.tva)}</td>
                        <td className="p-3 text-right text-orange-500">-{formatPrice(data.spf_cost)}</td>
                        <td className="p-3 text-right text-amber-500">{data.affiliate_cost > 0 ? `-${formatPrice(data.affiliate_cost)}` : '-'}</td>
                        <td className={`p-3 text-right font-bold bg-emerald-50 ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatPrice(data.profit)}</td>
                        <td className="p-3 text-right">{data.margin_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* PARTENAIRE MARQUE BLANCHE */}
            <Card>
              <CardHeader className="bg-indigo-600 text-white">
                <CardTitle>PARTENAIRE MARQUE BLANCHE - 2 000€ TTC/an</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Scénario (nb partenaires)</th>
                      <th className="text-right p-3">Prix Total TTC</th>
                      <th className="text-right p-3">Stripe</th>
                      <th className="text-right p-3">TVA</th>
                      <th className="text-right p-3 bg-emerald-100">Bénéfice</th>
                      <th className="text-right p-3">Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(estimations?.estimations?.partner_subscription || {}).map(([key, data]) => (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">
                          {key === 'marque_blanche_an1'         ? '1ère année (500€ promo)' :
                           key === 'marque_blanche_an1_affilie' ? '1ère année + Affilié' :
                           key === 'marque_blanche_an2'         ? 'Année 2+ (2 000€/an)' :
                           key === 'marque_blanche_an2_affilie' ? 'Année 2+ + Affilié' :
                           key === 'marque_blanche_x3'          ? '× 3 partenaires/an' :
                           key === 'marque_blanche_x5'          ? '× 5 partenaires/an' :
                           key === 'marque_blanche_x10'         ? '× 10 partenaires/an' : key}
                        </td>
                        <td className="p-3 text-right font-medium text-indigo-600">+{formatPrice(data.price_ttc)}</td>
                        <td className="p-3 text-right text-red-500">-{formatPrice(data.stripe_fee)}</td>
                        <td className="p-3 text-right text-purple-500">-{formatPrice(data.tva)}</td>
                        <td className={`p-3 text-right font-bold bg-emerald-50 ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatPrice(data.profit)}</td>
                        <td className="p-3 text-right">{data.margin_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* CE QUE GAGNE UN AFFILIÉ */}
            <Card>
              <CardHeader className="bg-yellow-500 text-white">
                <CardTitle>CE QUE GAGNE UN AFFILIÉ (15% du HT) — sur chaque vente</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3">Produit vendu</th>
                      <th className="text-right p-3">Prix TTC client</th>
                      <th className="text-right p-3">Base HT</th>
                      <th className="text-right p-3 bg-yellow-50">Commission affilié (15%)</th>
                      <th className="text-right p-3">Ton coût</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimations?.affiliate_gains && Object.entries(estimations.affiliate_gains).map(([key, gain]) => {
                      const priceMap = {
                        flash: 97, pro: 247, master: 497, radar: 49,
                        marque_blanche_an1: 500, marque_blanche_an2: 2000
                      };
                      const labelMap = {
                        flash: 'FLASH (audit seul)',
                        pro: 'MARCHAND PRO (abonnement)',
                        master: 'MASTER VIP (abonnement)',
                        radar: 'Radar Foncier (forfait)',
                        marque_blanche_an1: 'Marque Blanche — 1ère année',
                        marque_blanche_an2: 'Marque Blanche — Année 2+'
                      };
                      const ttc = priceMap[key] || 0;
                      const ht = Math.round(ttc / 1.20 * 100) / 100;
                      return (
                        <tr key={key} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{labelMap[key] || key}</td>
                          <td className="p-3 text-right">{formatPrice(ttc)}</td>
                          <td className="p-3 text-right text-gray-500">{formatPrice(ht)}</td>
                          <td className="p-3 text-right font-bold text-yellow-600 bg-yellow-50">{formatPrice(gain)}</td>
                          <td className="p-3 text-right text-red-500">-{formatPrice(gain)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

          </div>
        </TabsContent>

        {/* === FACTURES CLIENTS === */}
        <TabsContent value="client-invoices">
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Euro className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">CA Total TTC</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">{formatPrice(clientInvoicesSummary?.total_ttc)}</p>
                  <p className="text-xs text-emerald-600/70">{clientInvoicesSummary?.invoice_count || 0} factures</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">CA Total HT</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{formatPrice(clientInvoicesSummary?.total_ht)}</p>
                </CardContent>
              </Card>
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">TVA Collectée</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">{formatPrice(clientInvoicesSummary?.total_tva)}</p>
                  <p className="text-xs text-purple-600/70">20% TVA</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">Nb Factures</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">{clientInvoicesSummary?.invoice_count || 0}</p>
                  <p className="text-xs text-amber-600/70">Paiements réussis</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 bg-white rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg">Liste des factures clients</h3>
              <div className="flex flex-wrap items-center gap-2" data-testid="client-invoices-export-bar">
                <Label className="text-sm text-gray-600">Export comptable :</Label>
                <Select value={exportYear} onValueChange={setExportYear}>
                  <SelectTrigger className="w-[110px] bg-white" data-testid="export-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tout</SelectItem>
                    {[0, 1, 2].map(i => { const y = (new Date().getFullYear() - i).toString(); return <SelectItem key={y} value={y}>{y}</SelectItem>; })}
                  </SelectContent>
                </Select>
                {exportYear !== 'all' && (
                  <Select value={exportMonth} onValueChange={setExportMonth}>
                    <SelectTrigger className="w-[130px] bg-white" data-testid="export-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Année entière</SelectItem>
                      {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button onClick={() => exportClientInvoices('csv')} variant="outline" size="sm" disabled={!!exporting} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" />{exporting === 'csv' ? 'Export…' : 'CSV'}
                </Button>
                <Button onClick={() => exportClientInvoices('zip')} size="sm" className="bg-navy text-white hover:bg-navy/90" disabled={!!exporting} data-testid="export-zip-btn">
                  <Download className="w-4 h-4 mr-2" />{exporting === 'zip' ? 'Export…' : 'ZIP (CSV + PDF)'}
                </Button>
                {exportYear !== 'all' && exportMonth !== 'all' && (
                  <Button onClick={sendPeriodToAccountant} size="sm" variant="outline" className="border-gold text-amber-700 hover:bg-gold/10" disabled={!!exporting} data-testid="send-period-to-accountant-btn" title="Envoie le CSV + ZIP de ce mois à l'email comptable défini dans Paramètres">
                    <Send className="w-4 h-4 mr-2" />{exporting === 'mail' ? 'Envoi…' : 'Envoyer au comptable'}
                  </Button>
                )}
                <Button onClick={fetchData} variant="ghost" size="sm">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Client Invoices Table */}
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-navy text-white">
                    <tr>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">N° Facture</th>
                      <th className="p-3 text-left">Client</th>
                      <th className="p-3 text-left">Formule</th>
                      <th className="p-3 text-right">Montant HT</th>
                      <th className="p-3 text-right">TVA</th>
                      <th className="p-3 text-right">Total TTC</th>
                      <th className="p-3 text-center">Statut</th>
                      <th className="p-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientInvoices.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500">
                          Aucune facture client pour le moment
                        </td>
                      </tr>
                    ) : (
                      clientInvoices.map((invoice) => (
                        <tr key={invoice.id} className="border-b hover:bg-gray-50" data-testid={`client-invoice-row-${invoice.id}`}>
                          <td className="p-3">{formatDate(invoice.created_at)}</td>
                          <td className="p-3 font-mono text-sm">
                            <span data-testid={`client-invoice-number-${invoice.id}`}>{invoice.invoice_number}</span>
                            {invoice.internal_ref && invoice.internal_ref !== invoice.invoice_number && (
                              <p className="text-[10px] text-gray-400 font-sans" title="Référence interne du paiement">{invoice.internal_ref}</p>
                            )}
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{invoice.customer_name || (invoice.customer_email ? invoice.customer_email.split('@')[0] : 'Client')}</p>
                              <p className="text-xs text-gray-500">{invoice.customer_email}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className="bg-blue-100 text-blue-700">{invoice.package_label}</Badge>
                          </td>
                          <td className="p-3 text-right">{formatPrice(invoice.amount_ht)}</td>
                          <td className="p-3 text-right text-purple-600">{formatPrice(invoice.tva_amount)}</td>
                          <td className="p-3 text-right font-bold">{formatPrice(invoice.amount_ttc)}</td>
                          <td className="p-3 text-center">
                            <Badge className="bg-emerald-100 text-emerald-700">Payé</Badge>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => previewClientInvoicePDF(invoice)}
                                title="Voir la facture"
                                data-testid={`preview-client-invoice-${invoice.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => downloadClientInvoicePDF(invoice)}
                                title="Télécharger la facture"
                                data-testid={`download-client-invoice-${invoice.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Info Box */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">À propos des factures clients</h4>
                    <p className="text-sm text-blue-700">
                      Ces factures sont générées automatiquement pour chaque paiement Stripe réussi. 
                      Elles utilisent les coordonnées de votre entreprise configurées dans les <strong>Paramètres</strong>.
                      Vos clients peuvent également accéder à leurs factures depuis leur espace personnel.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === NOTES DE FRAIS === */}
        <TabsContent value="commissions">
          <div className="space-y-4">
            {/* Header Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-lg shadow-sm">
              <div className="flex items-center gap-4">
                <span className="font-medium">Filtrer:</span>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('all')}
                  >
                    Tous ({allCommissions.length})
                  </Button>
                  <Button 
                    size="sm" 
                    variant={filterStatus === 'pending' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('pending')}
                    className={filterStatus === 'pending' ? 'bg-red-500' : ''}
                  >
                    À valider ({allCommissions.filter(c => c.status === 'pending').length})
                  </Button>
                  <Button 
                    size="sm" 
                    variant={filterStatus === 'validated' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('validated')}
                    className={filterStatus === 'validated' ? 'bg-amber-500' : ''}
                  >
                    À payer ({allCommissions.filter(c => c.status === 'validated').length})
                  </Button>
                  <Button 
                    size="sm" 
                    variant={filterStatus === 'paid' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('paid')}
                    className={filterStatus === 'paid' ? 'bg-emerald-500' : ''}
                  >
                    Payé ({allCommissions.filter(c => c.status === 'paid').length})
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-1" />Actualiser
                </Button>
                <Button variant="destructive" size="sm" onClick={deleteAllCommissions}>
                  <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                </Button>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedCommissions.length > 0 && (
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <span className="font-medium">{selectedCommissions.length} sélectionné(s)</span>
                <Button size="sm" onClick={bulkValidate} className="bg-blue-500 hover:bg-blue-600">
                  <Check className="w-4 h-4 mr-1" />Valider
                </Button>
                <Button size="sm" onClick={bulkPay} className="bg-emerald-500 hover:bg-emerald-600">
                  <DollarSign className="w-4 h-4 mr-1" />Payer
                </Button>
                <Button size="sm" variant="destructive" onClick={bulkDelete}>
                  <Trash2 className="w-4 h-4 mr-1" />Supprimer
                </Button>
              </div>
            )}

            {/* Commissions Table */}
            <Card>
              <CardContent className="p-0">
                {filteredCommissions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Receipt className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucune note de frais</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 w-10">
                          <Checkbox 
                            checked={selectedCommissions.length === filteredCommissions.length && filteredCommissions.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedCommissions(filteredCommissions.map(c => c.id));
                              else setSelectedCommissions([]);
                            }}
                          />
                        </th>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Bénéficiaire</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-right p-3">Montant HT</th>
                        <th className="text-right p-3">TVA</th>
                        <th className="text-right p-3">TTC</th>
                        <th className="text-center p-3">Statut</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCommissions.map(commission => (
                        <tr key={commission.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <Checkbox 
                              checked={selectedCommissions.includes(commission.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedCommissions([...selectedCommissions, commission.id]);
                                else setSelectedCommissions(selectedCommissions.filter(id => id !== commission.id));
                              }}
                            />
                          </td>
                          <td className="p-3 text-sm">{formatDate(commission.created_at)}</td>
                          <td className="p-3">
                            <p className="font-medium">{commission.beneficiary_name}</p>
                            <p className="text-xs text-muted-foreground">{commission.beneficiary_email}</p>
                          </td>
                          <td className="p-3">
                            <Badge className={
                              commission.type === 'affiliate' ? 'bg-amber-100 text-amber-700' :
                              commission.type === 'analyst' ? 'bg-blue-100 text-blue-700' :
                              commission.type === 'chiffreur' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-purple-100 text-purple-700'
                            }>
                              {commission.type === 'affiliate' ? 'Affilié' : 
                               commission.type === 'analyst' ? 'Analyste' : 
                               commission.type === 'chiffreur' ? 'Chiffreur' : 'Partenaire'}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm max-w-xs truncate">{commission.source_description}</td>
                          <td className="p-3 text-right">{formatPrice(commission.amount_ht)}</td>
                          <td className="p-3 text-right text-purple-600">{formatPrice(commission.tva_amount)}</td>
                          <td className="p-3 text-right font-bold">{formatPrice(commission.amount_ttc)}</td>
                          <td className="p-3 text-center">{getStatusBadge(commission.status)}</td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => previewCommissionPDF(commission)}
                                title="Voir PDF"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => downloadCommissionPDF(commission)}
                                title="Télécharger PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              {commission.status !== 'paid' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    setEditingCommission(commission);
                                    setEditCommissionModal(true);
                                  }}
                                  title="Modifier"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              {commission.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-blue-500"
                                  onClick={() => validateCommission(commission.id)}
                                  title="Valider"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              {commission.status === 'validated' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-emerald-500"
                                  onClick={() => payCommission(commission.id)}
                                  title="Marquer payé"
                                >
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500"
                                onClick={() => deleteCommission(commission.id)}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === COLLABORATEURS === */}
        <TabsContent value="details">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Détail par Collaborateur</h2>
              <Button variant="destructive" size="sm" onClick={deleteAllCommissions}>
                <Trash2 className="w-4 h-4 mr-1" />Reset toutes les notes
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Affiliés */}
              {(filterMemberType === 'all' || filterMemberType === 'affiliate') && (
              <Card>
                <CardHeader className="bg-amber-50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Link2 className="w-5 h-5" />
                      Affiliés ({details.affiliates?.length || 0})
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      {formatPrice((byType.affiliate?.total || 0))} total
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-80 overflow-y-auto">
                  {details.affiliates?.length > 0 ? (
                    <div className="divide-y">
                      {details.affiliates.map(aff => (
                        <div 
                          key={aff.id} 
                          className="p-4 hover:bg-amber-50 cursor-pointer transition-colors"
                          onClick={() => openCollaboratorDetail(aff, 'affiliate')}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-amber-700">{aff.user?.name || aff.beneficiary_name || aff.code}</p>
                              {(aff.user?.name || aff.beneficiary_name) && <p className="text-xs text-amber-500 font-mono">{aff.code}</p>}
                              <p className="text-xs text-muted-foreground">{aff.commission_count} note(s)</p>
                            </div>
                            <p className="font-bold text-amber-600">{formatPrice(aff.total_earnings)}</p>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline" className="bg-red-50 text-red-700">{formatPrice(aff.pending)}</Badge>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">{formatPrice(aff.validated)}</Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{formatPrice(aff.paid)}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">Aucun affilié</p>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Analystes */}
              {(filterMemberType === 'all' || filterMemberType === 'analyst') && (
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-700">
                      <UserCheck className="w-5 h-5" />
                      Analystes ({details.analysts?.length || 0})
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">
                      {formatPrice((byType.analyst?.total || 0))} total
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-80 overflow-y-auto">
                  {details.analysts?.length > 0 ? (
                    <div className="divide-y">
                      {details.analysts.map(an => (
                        <div 
                          key={an.id} 
                          className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => openCollaboratorDetail(an, 'analyst')}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-blue-700">{an.name}</p>
                              <p className="text-xs text-muted-foreground">{an.email}</p>
                            </div>
                            <p className="font-bold text-blue-600">{formatPrice(an.total_earnings)}</p>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline" className="bg-red-50 text-red-700">{formatPrice(an.pending)}</Badge>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">{formatPrice(an.validated)}</Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{formatPrice(an.paid)}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">Aucun analyste</p>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Chiffreurs */}
              {(filterMemberType === 'all' || filterMemberType === 'chiffreur') && (
              <Card>
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Calculator className="w-5 h-5" />
                      Chiffreurs ({details.chiffreurs?.length || 0})
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {formatPrice((byType.chiffreur?.total || 0))} total
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-80 overflow-y-auto">
                  {details.chiffreurs?.length > 0 ? (
                    <div className="divide-y">
                      {details.chiffreurs.map(ch => (
                        <div 
                          key={ch.id} 
                          className="p-4 hover:bg-emerald-50 cursor-pointer transition-colors"
                          onClick={() => openCollaboratorDetail(ch, 'chiffreur')}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-emerald-700">{ch.name}</p>
                              <p className="text-xs text-muted-foreground">{ch.email}</p>
                            </div>
                            <p className="font-bold text-emerald-600">{formatPrice(ch.total_earnings)}</p>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline" className="bg-red-50 text-red-700">{formatPrice(ch.pending)}</Badge>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">{formatPrice(ch.validated)}</Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{formatPrice(ch.paid)}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">Aucun chiffreur</p>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Partenaires */}
              {(filterMemberType === 'all' || filterMemberType === 'partner') && (
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-purple-700">
                      <Building2 className="w-5 h-5" />
                      Partenaires ({details.partners?.length || 0})
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">
                      {formatPrice((byType.partner?.total || 0))} total
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-80 overflow-y-auto">
                  {details.partners?.length > 0 ? (
                    <div className="divide-y">
                      {details.partners.map(p => (
                        <div 
                          key={p.id} 
                          className="p-4 hover:bg-purple-50 cursor-pointer transition-colors"
                          onClick={() => openCollaboratorDetail(p, 'partner')}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-purple-700">{p.name || p.subdomain}</p>
                              <p className="text-xs text-muted-foreground">{p.subdomain}.oeil-marchand.com</p>
                            </div>
                            <p className="font-bold text-purple-600">{formatPrice(p.total_earnings)}</p>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline" className="bg-red-50 text-red-700">{formatPrice(p.pending)}</Badge>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">{formatPrice(p.validated)}</Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{formatPrice(p.paid)}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">Aucun partenaire</p>
                  )}
                </CardContent>
              </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* === MODAL FICHE COLLABORATEUR === */}
      <Dialog open={collaboratorModal} onOpenChange={setCollaboratorModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCollaborator?.collaborator_type === 'affiliate' && <Link2 className="w-5 h-5 text-amber-500" />}
              {selectedCollaborator?.collaborator_type === 'analyst' && <UserCheck className="w-5 h-5 text-blue-500" />}
              {selectedCollaborator?.collaborator_type === 'chiffreur' && <Calculator className="w-5 h-5 text-emerald-500" />}
              {selectedCollaborator?.collaborator_type === 'partner' && <Building2 className="w-5 h-5 text-purple-500" />}
              Fiche {selectedCollaborator?.name || selectedCollaborator?.code || selectedCollaborator?.subdomain}
            </DialogTitle>
          </DialogHeader>

          {selectedCollaborator && (
            <div className="space-y-6">
              {/* Info Card */}
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Nom</p>
                      <p className="font-medium">{selectedCollaborator.name || selectedCollaborator.code || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedCollaborator.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Type</p>
                      <Badge className={
                        selectedCollaborator.collaborator_type === 'affiliate' ? 'bg-amber-100 text-amber-700' :
                        selectedCollaborator.collaborator_type === 'analyst' ? 'bg-blue-100 text-blue-700' :
                        selectedCollaborator.collaborator_type === 'chiffreur' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-purple-100 text-purple-700'
                      }>
                        {selectedCollaborator.collaborator_type === 'affiliate' ? 'Affilié' :
                         selectedCollaborator.collaborator_type === 'analyst' ? 'Analyste' :
                         selectedCollaborator.collaborator_type === 'chiffreur' ? 'Chiffreur' : 'Partenaire'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Statut TVA</p>
                      <p className="font-medium">Particulier</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-red-600 mb-1">À valider</p>
                    <p className="text-2xl font-bold text-red-600">{formatPrice(selectedCollaborator.pending)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-amber-600 mb-1">À payer</p>
                    <p className="text-2xl font-bold text-amber-600">{formatPrice(selectedCollaborator.validated)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-emerald-600 mb-1">Payé</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatPrice(selectedCollaborator.paid)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Commissions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Historique des Notes de Frais</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(() => {
                    const collaboratorCommissions = getCollaboratorCommissions(selectedCollaborator.id);
                    if (collaboratorCommissions.length === 0) {
                      return (
                        <div className="text-center py-8 text-muted-foreground">
                          <Receipt className="w-8 h-8 mx-auto mb-2 opacity-20" />
                          <p>Aucune note de frais</p>
                        </div>
                      );
                    }
                    return (
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Description</th>
                            <th className="text-right p-3">Montant HT</th>
                            <th className="text-right p-3">TVA</th>
                            <th className="text-right p-3">TTC</th>
                            <th className="text-center p-3">Statut</th>
                            <th className="text-right p-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {collaboratorCommissions.map(commission => (
                            <tr key={commission.id} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-sm">{formatDate(commission.created_at)}</td>
                              <td className="p-3 text-sm">{commission.source_description}</td>
                              <td className="p-3 text-right">{formatPrice(commission.amount_ht)}</td>
                              <td className="p-3 text-right text-purple-600">{formatPrice(commission.tva_amount)}</td>
                              <td className="p-3 text-right font-bold">{formatPrice(commission.amount_ttc)}</td>
                              <td className="p-3 text-center">{getStatusBadge(commission.status)}</td>
                              <td className="p-3">
                                <div className="flex justify-end gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => previewCommissionPDF(commission)}
                                    title="Voir PDF"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => downloadCommissionPDF(commission)}
                                    title="Télécharger PDF"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  {commission.status !== 'paid' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setEditingCommission(commission);
                                        setEditCommissionModal(true);
                                      }}
                                      title="Modifier"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {commission.status === 'pending' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-blue-500"
                                      onClick={() => validateCommission(commission.id)}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {commission.status === 'validated' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-emerald-500"
                                      onClick={() => payCommission(commission.id)}
                                    >
                                      <DollarSign className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-500"
                                    onClick={() => deleteCommission(commission.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === MODAL MODIFIER COMMISSION === */}
      <Dialog open={editCommissionModal} onOpenChange={setEditCommissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la Note de Frais</DialogTitle>
          </DialogHeader>
          {editingCommission && (
            <div className="space-y-4">
              <div>
                <Label>Description</Label>
                <Input 
                  value={editingCommission.source_description || ''}
                  onChange={(e) => setEditingCommission({...editingCommission, source_description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Montant HT (€)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={editingCommission.amount_ht || 0}
                    onChange={(e) => {
                      const ht = parseFloat(e.target.value) || 0;
                      const tva = ht * (editingCommission.tva_rate || 0);
                      setEditingCommission({
                        ...editingCommission, 
                        amount_ht: ht,
                        tva_amount: tva,
                        amount_ttc: ht + tva
                      });
                    }}
                  />
                </div>
                <div>
                  <Label>TVA (%)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={(editingCommission.tva_rate || 0) * 100}
                    onChange={(e) => {
                      const rate = (parseFloat(e.target.value) || 0) / 100;
                      const tva = editingCommission.amount_ht * rate;
                      setEditingCommission({
                        ...editingCommission, 
                        tva_rate: rate,
                        tva_amount: tva,
                        amount_ttc: editingCommission.amount_ht + tva
                      });
                    }}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input 
                  value={editingCommission.notes || ''}
                  onChange={(e) => setEditingCommission({...editingCommission, notes: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditCommissionModal(false)}>Annuler</Button>
                <Button onClick={updateCommission}>Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
